import { useCallback, useEffect, useState } from "react";

import { Heading } from "@magnetic/heading";
import { Input } from "@magnetic/input";
import { Toggle } from "@magnetic/toggle";

import { Modal } from "src/components";
import { Button } from "src/components/buttons";
import { ErrorNotification } from "src/components/errorNotification";
import { HFlex } from "src/components/hFlex";
import { LabelFieldContainer } from "src/components/labelFieldContainer";
import { VFlex } from "src/components/vFlex";
import { ApiError } from "src/core/apiError";
import { Brig } from "src/gen/rest/brig/BrigService";
import { SetUsersRequest, UsersResponse } from "src/gen/schema/brig/bapi";
import { UserRole } from "src/gen/schema/models/auth";
import { User } from "src/gen/schema/models/user";
import { BOTS_PROVIDER } from "src/pages/administration/constants";

interface AddBotProps {
  readonly show: boolean;
  readonly onClose: () => void;
  readonly onSuccess: (response: UsersResponse) => void;
}

export const AddBotModal = ({ show, onClose, onSuccess }: AddBotProps) => {
  const [error, setError] = useState<ApiError | undefined>();
  const [name, setName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [enabled, setEnabled] = useState<boolean>(true);

  const handleAdd = useCallback(() => {
    const trimmedName = name.trim();
    setError(undefined);
    setLoading(true);
    const botUser: User = User.create({
      email: trimmedName,
      provider: BOTS_PROVIDER,
      role: UserRole.READ_ONLY as number,
      enabled
    });

    Brig.setUsers(SetUsersRequest.create({ users: [botUser] }))
      .then((response) => {
        onSuccess(response);
        onClose();
      })
      .catch(setError)
      .finally(() => {
        setLoading(false);
      });
  }, [name, enabled, onClose, onSuccess]);

  const handleNameChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      setName(e.currentTarget.value);
      if (error) {
        setError(undefined);
      }
    },
    [error]
  );

  const handleEnableChange = useCallback(() => {
    setEnabled((val) => !val);
  }, []);

  // Reset form when modal is closed
  useEffect(() => {
    if (!show) {
      setName("");
      setEnabled(true);
      setError(undefined);
    }
  }, [show]);

  return (
    <Modal className="add-bot-modal" isOpen={show} size="lg">
      <VFlex>
        <HFlex
          align="center"
          gap="sm"
          margin={{
            bottom: 16
          }}
        >
          <Heading as="h2" size="primary">
            Add bot user
          </Heading>
        </HFlex>
        <ErrorNotification error={error} />
        <Input
          label="Name"
          name="name"
          value={name}
          onChange={handleNameChange}
          placeholder="Enter bot name"
          required={true}
          autoComplete="off"
        />
        <LabelFieldContainer label="Status" name="status" size="md">
          <Toggle name="status" onClick={handleEnableChange} checked={enabled}>
            <span id="status-desc">{enabled ? "Enabled" : "Disabled"}</span>
          </Toggle>
        </LabelFieldContainer>
        <HFlex justify="flex-end">
          <Button.Group>
            <Button kind="tertiary" name="modal-cancel" onClick={onClose}>
              Cancel
            </Button>
            <Button
              name="modal-save"
              onClick={handleAdd}
              disabled={!name.trim() || loading}
            >
              Save
            </Button>
          </Button.Group>
        </HFlex>
      </VFlex>
    </Modal>
  );
};
